import { useMemo, useState } from 'react';
import type { ProductKind } from '@salonomy/types';
import { Badge, Card, EmptyState, Field, Pill, Select, Spinner, Toggle } from '@salonomy/ui';
import { useStockLevels } from '../../data/stock.js';
import { money } from '../../lib/format.js';
import './stock.css';

const KIND_FILTERS: Array<{ value: '' | ProductKind; label: string }> = [
  { value: '', label: 'All kinds' },
  { value: 'retail', label: 'Retail' },
  { value: 'professional', label: 'Professional' },
];

/**
 * Stock levels — one row per ACTIVE product from `useStockLevels`: on-hand, a low-stock Badge, and the
 * server's `stockValuePence` per row, summed into a footer valuation total. Every figure is the
 * server's: on-hand is SUM(qty_delta) and the value is the server's (cost × on-hand) — nothing is
 * recomputed here. `stockValuePence` is SignedPence, so a row (and the total) can be NEGATIVE when a
 * product is oversold; money() formats the sign faithfully and we never clamp it to zero.
 */
export function StockLevelsPanel() {
  const [kind, setKind] = useState<'' | ProductKind>('');
  const [lowStockOnly, setLowStockOnly] = useState(false);

  const { data, isLoading, isError } = useStockLevels({
    kind: kind || undefined,
    lowStockOnly: lowStockOnly || undefined,
  });

  const rows = data ?? [];
  // Sum the server's per-row signed values — the total may be negative; we never assume non-negative.
  const totalValue = useMemo(() => rows.reduce((sum, r) => sum + r.stockValuePence, 0), [rows]);
  const lowCount = useMemo(() => rows.filter((r) => r.isLow).length, [rows]);

  return (
    <Card>
      <div className="stock-head">
        <div>
          <div className="section-title" style={{ marginBottom: 2 }}>
            Stock levels
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            On-hand and value per active product. Value is the server&rsquo;s (cost × on-hand) — it
            can be negative if oversold.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 150 }}>
            <Field label="Kind">
              <Select value={kind} onChange={(e) => setKind(e.target.value as '' | ProductKind)}>
                {KIND_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </div>
      </div>

      <div className="stock-toggles">
        <Toggle checked={lowStockOnly} onChange={setLowStockOnly} label="Low stock only" />
        {lowCount > 0 ? <Pill tone="danger">{lowCount} low</Pill> : null}
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
          <Spinner size={16} />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading stock levels…
          </span>
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Couldn&rsquo;t load stock levels. They read live from your movements — try again.
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No stock-tracked products"
          description="Levels appear once you have active products with stock tracking on."
        />
      ) : (
        <div
          className="stock-table"
          style={{ ['--cols' as string]: 'minmax(0,2fr) 1fr 0.9fr 0.9fr 1.2fr' }}
        >
          <div className="stock-row stock-row--head">
            <span>Product</span>
            <span>Kind</span>
            <span className="stock-num">On-hand</span>
            <span>Status</span>
            <span className="stock-num">Value</span>
          </div>
          {rows.map((r) => (
            <div key={r.productId} className="stock-row">
              <span className="stock-cell-main__name">{r.name}</span>
              <span>{r.kind === 'retail' ? 'Retail' : 'Professional'}</span>
              <span className="stock-num mono">{r.onHand}</span>
              <span>{r.isLow ? <Badge>Low</Badge> : <span className="muted">OK</span>}</span>
              <span className="stock-num mono">{money(r.stockValuePence)}</span>
            </div>
          ))}
          <div className="stock-row stock-row--foot">
            <span>Total stock valuation</span>
            <span />
            <span />
            <span />
            <span className="stock-num mono">{money(totalValue)}</span>
          </div>
        </div>
      )}
    </Card>
  );
}
